<template>
  <div class="table-pagination eds-pagination">
    <div class="eds-tab tab-compact">
      <a
        href="#"
        data-first
        class="tab-content eds-link"
        v-bind="{ disabled: selected === 1 ? true : null }"
        @click.prevent="selectFirstPage()"
        @keyup.enter="selectFirstPage()"
      >
        <em class="tab-icon material-icons"> first_page </em>
      </a>
    </div>
    <div class="eds-tab tab-compact">
      <a
        href="#"
        data-previous
        class="tab-content eds-link"
        v-bind="{ disabled: selected === 1 ? true : null }"
        @click.prevent="prevPage()"
        @keyup.enter="prevPage()"
      >
        <em class="tab-icon material-icons">chevron_left</em>
      </a>
    </div>
    <template
      v-for="(page, index) in pages"
      :key="page.index"
    >
      <div
        class="eds-tab tab-compact"
        :class="[{ 'tab-selected': page.selected }]"
      >
        <a
          v-if="page.breakView"
          class="tab-content eds-link"
          tabindex="0"
          @click="selected < index ? nextPage() : prevPage()"
          ><slot name="breakViewContent">{{ breakViewText }}</slot></a
        >
        <a
          v-else
          :data-page="page.index"
          href="#"
          class="tab-content eds-link"
          :class="[{ 'link-selected': page.selected }]"
          @click.prevent="handlePageSelected(page.index + 1)"
          @keyup.enter="handlePageSelected(page.index + 1)"
          >{{ page.content }}</a
        >
      </div>
    </template>
    <div class="eds-tab tab-compact">
      <a
        href="#"
        data-next
        class="tab-content eds-link"
        v-bind="{ disabled: selected === pageCount ? true : null }"
        @click.prevent="nextPage()"
        @keyup.enter="nextPage()"
      >
        <em class="tab-icon material-icons">chevron_right</em>
      </a>
    </div>
    <div class="eds-tab tab-compact">
      <a
        href="#"
        data-last
        class="tab-content eds-link"
        v-bind="{ disabled: selected === pageCount ? true : null }"
        @click.prevent="selectLastPage()"
        @keyup.enter="selectLastPage()"
      >
        <em class="tab-icon material-icons">last_page</em>
      </a>
    </div>
    <div
      v-if="totalRows > 0"
      class="range"
      id="range"
    >
      {{ (selected - 1) * pageSize + 1 }} -
      {{ selected === pageCount ? totalRows : pageSize * selected }}
      of
      {{ totalRows }}
    </div>
    <div class="pagesize-label">Rows per page</div>
    <Select
      v-model="rowsPerPage"
      field-control-class="atb-w-16 atb-mr-2"
      :options="options"
      @update:modelValue="$emit('pageSizeChange', rowsPerPage)"
    >
      <template v-slot:option="{ item }">
        {{ item.name }}
      </template>
    </Select>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import Select from '@atb-ui/eds-vue/src/components/Select.vue';

  interface Page {
    index?: number;
    content?: number;
    selected?: boolean;
    disabled?: boolean;
    breakView?: boolean;
  }

  interface Pages {
    [key: number]: Page;
  }

  export default defineComponent({
    name: 'Pagination',
    emits: ['pageClick', 'pageSizeChange', 'update:modelValue'],
    components: {
      Select,
    },
    props: {
      modelValue: {
        type: Number,
      },
      pageSize: {
        type: Number,
        required: false,
        default: 10,
      },
      totalRows: {
        type: Number,
        required: true,
      },
      pageCount: {
        type: Number,
        required: true,
      },
      pageRange: {
        type: Number,
        default: 3,
      },
      marginPages: {
        type: Number,
        default: 1,
      },
      breakViewText: {
        type: String,
        default: '…',
      },
    },
    data() {
      return {
        rowsPerPage: this.pageSize,
        innerValue: 1,
        options: [
          { name: '20' },
          { name: '50' },
          { name: '100' },
          { name: '250' },
          { name: '500' },
          { name: 'ALL' },
        ],
      };
    },
    computed: {
      selected: {
        get: function (): number {
          return this.modelValue || this.innerValue;
        },
        set: function (newValue: number) {
          this.innerValue = newValue;
        },
      },
      /**referred from vuejs-paginate https://www.npmjs.com/package/vuejs-paginate */
      pages: function (): any {
        let items = {} as Pages;
        if (this.pageCount <= this.pageRange) {
          for (let index = 0; index < this.pageCount; index++) {
            let page = {
              index: index,
              content: index + 1,
              selected: index === this.selected - 1,
            } as Page;
            items[index] = page;
          }
        } else {
          const halfPageRange = Math.floor(this.pageRange / 2);

          let setPageItem = (index: number) => {
            let page = {
              index: index,
              content: index + 1,
              selected: index === this.selected - 1,
            } as Page;

            items[index] = page;
          };

          let setBreakView = (index: number) => {
            let breakView = {
              disabled: true,
              breakView: true,
            } as Page;

            items[index] = breakView;
          };

          // 1st - loop thru low end of margin pages
          for (let i = 0; i < this.marginPages; i++) {
            setPageItem(i);
          }

          // 2nd - loop thru selected range
          let selectedRangeLow = 0;
          if (this.selected - halfPageRange > 0) {
            selectedRangeLow = this.selected - 1 - halfPageRange;
          }

          let selectedRangeHigh = selectedRangeLow + this.pageRange - 1;
          if (selectedRangeHigh >= this.pageCount) {
            selectedRangeHigh = this.pageCount - 1;
            selectedRangeLow = selectedRangeHigh - this.pageRange + 1;
          }

          for (
            let i = selectedRangeLow;
            i <= selectedRangeHigh && i <= this.pageCount - 1;
            i++
          ) {
            setPageItem(i);
          }

          // Check if there is breakView in the left of selected range
          if (selectedRangeLow > this.marginPages) {
            setBreakView(selectedRangeLow - 1);
          }

          // Check if there is breakView in the right of selected range
          if (selectedRangeHigh + 1 < this.pageCount - this.marginPages) {
            setBreakView(selectedRangeHigh + 1);
          }

          // 3rd - loop thru high end of margin pages
          for (
            let i = this.pageCount - 1;
            i >= this.pageCount - this.marginPages;
            i--
          ) {
            setPageItem(i);
          }
        }
        return items;
      },
    },
    methods: {
      handlePageSelected(selected: number, _marginPage?: string): void {
        if (this.selected === selected) return;

        this.innerValue = selected;
        this.$emit('update:modelValue', selected);
        this.$emit('pageClick', selected);
      },
      prevPage(): void {
        if (this.selected <= 1) return;
        this.handlePageSelected(this.selected - 1, 'prevPage');
      },
      nextPage(): void {
        if (this.selected >= this.pageCount) return;
        this.handlePageSelected(this.selected + 1, 'nextPage');
      },
      selectFirstPage(): void {
        if (this.selected <= 1) return;
        this.handlePageSelected(1, 'firstPage');
      },
      selectLastPage(): void {
        if (this.selected >= this.pageCount) return;
        this.handlePageSelected(this.pageCount, 'lastPage');
      },
    },
  });
</script>

<style scoped>
  a.tab-content.eds-link[disabled] {
    --text-opacity: 1;
    color: #8c8c8c;
    color: rgba(140, 140, 140, var(--text-opacity));
    cursor: default;
  }
</style>
